/**
 * paymentMethodsAdmin.service.ts — Admin > Payment Methods: the payment types
 * the practice accepts (payment_methods — cash, check, financing…), with each
 * one's classification, what it can be used for, surcharge and loan terms,
 * through /v5/payment-methods as the corporate dashboard edits them.
 *
 * Not patients' saved cards, and not the cards Prospera bills the practice
 * with (Admin > Billing). Checkout and quotes read the same rows through
 * payments.service / quotes.service. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type SurchargeType = 'flat' | 'percent';

/** payment_methods.typeIntentID: where the method can be used. */
export const USABLE_FOR_OPTIONS = [
  { value: 0, label: 'All Payments', short: 'All Payments' },
  { value: 1, label: 'Manually Recorded Payments', short: 'Manually Recorded' },
  { value: 2, label: 'Charged Payments', short: 'Charged Payments' },
] as const;

export interface LoanTerm {
  /** Months. */
  term: number;
  /** Percent. */
  apr: number;
}

export interface AdminPaymentMethod {
  paymentMethodID: number;
  name: string;
  hint: string;
  /** payment_methods_enum id; 0 = none. */
  pmEnumID: number;
  classification: string;
  typeIntentID: number;
  surchargeAmount: number;
  surchargeType: SurchargeType;
  terms: LoanTerm[];
  active: boolean;
}

export interface PaymentMethodClassification {
  pmEnumID: number;
  title: string;
  active: boolean;
}

export interface PaymentMethodPayload {
  name: string;
  hint: string;
  pmEnumID: number;
  typeIntentID: number;
  surchargeAmount: number;
  surchargeType: SurchargeType;
  terms: LoanTerm[];
}

function flag(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}

/** Every method, deleted ones too, in the account's order. */
export function listAdminPaymentMethods(): Promise<AdminPaymentMethod[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/payment-methods', { params: { include_inactive: 1 } })
    .then((r) =>
      (Array.isArray(r.data?.data) ? r.data.data : []).map((row) => ({
        paymentMethodID: Number(row.paymentMethodID ?? 0),
        name: String(row.name ?? ''),
        hint: String(row.hint ?? ''),
        pmEnumID: Number(row.pmEnumID ?? 0) || 0,
        classification: String(row.classification ?? ''),
        typeIntentID: Number(row.typeIntentID ?? 0) || 0,
        surchargeAmount: Number(row.surchargeAmount ?? 0) || 0,
        surchargeType: row.surchargeType === 'percent' ? 'percent' : 'flat',
        terms: (Array.isArray(row.terms) ? row.terms : []).map((t: Record<string, unknown>) => ({
          term: Number(t.term ?? 0),
          apr: Number(t.apr ?? 0),
        })),
        active: flag(row.active),
      })),
    );
}

/** The account's classifications, live ones (for the select). */
export function listPaymentMethodClassifications(): Promise<PaymentMethodClassification[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/payment-methods/classifications').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : []).map((c) => ({
      pmEnumID: Number(c.pmEnumID ?? 0),
      title: String(c.title ?? ''),
      active: c.active === undefined ? true : flag(c.active),
    })),
  );
}

export function createPaymentMethod(data: PaymentMethodPayload): Promise<void> {
  return apiClient.post('/payment-methods', data).then(() => undefined);
}

export function updatePaymentMethod(id: number, data: PaymentMethodPayload): Promise<void> {
  return apiClient.put(`/payment-methods/${id}`, data).then(() => undefined);
}

export function deletePaymentMethod(id: number): Promise<void> {
  return apiClient.delete(`/payment-methods/${id}`).then(() => undefined);
}

export function restorePaymentMethod(id: number): Promise<void> {
  return apiClient.post(`/payment-methods/${id}/restore`).then(() => undefined);
}

/** Corp's Surcharge cell: "—", "N%" or "$N". */
export function formatSurcharge(amount: number, type: SurchargeType): string {
  if (!(amount > 0)) return '—';
  return type === 'percent' ? `${amount}%` : `$${amount}`;
}

/** Corp's Loan Terms cell: "12mo @ 9.99%, 24mo @ 0%" (+N more after two). */
export function formatTerms(terms: LoanTerm[]): string {
  if (terms.length === 0) return '—';
  const shown = terms.slice(0, 2).map((t) => `${t.term}mo @ ${t.apr}%`).join(', ');
  return terms.length > 2 ? `${shown} +${terms.length - 2} more` : shown;
}
